import { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'

import { ActionFlag, BuilderDialog } from '@makyoapp/makyo-common'
import { Dialog16 } from '@makyoapp/makyo-icon/icon'
import { Button, Tabs } from '@makyoapp/makyo-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { ComponentPanelTab } from '@/shared/builder-ui'
import { BackButton } from '@/components/BackButton'

import { SearchField } from '../PagePanel/SearchField'
import { DialogSettingAction } from './DialogSettingAction'
import { DialogSettingBehavior } from './DialogSettingBehavior'
import { DialogSettingData } from './DialogSettingData'
import { DialogSettingDesign } from './DialogSettingDesign'

/**
 * @todo - only allow 2 actions: close dialog box & perform next action
 */
function DialogPanelEdit({ dialog: { id: dialogId } }: { dialog: BuilderDialog }) {
  const { dialogs, setDialogAction } = useAppBuilderContext()

  const dialogIdx = useMemo(() => {
    return dialogs.findIndex((d) => d.id === dialogId)
  }, [dialogs, dialogId])

  const dialog = dialogs[dialogIdx]

  // parsing dialog name
  const dialogName = useVariableTextSingleCell(dialog.name)

  const [searchData, setSearchData] = useState('')
  const [searchDesign, setSearchDesign] = useState('')
  const [searchAction, setSearchAction] = useState('')
  const [searchBehavior, setSearchBehavior] = useState('')

  let initialActiveKey: ComponentPanelTab.Data = ComponentPanelTab.Data

  const [activeKey, setActiveKey] = useState<ComponentPanelTab>(initialActiveKey)

  const renderReviewButton = () => {
    return (
      <div className='px-4 pb-5'>
        <Button
          className='w-full'
          icon={<Dialog16 />}
          onClick={() => {
            setDialogAction({
              flag: ActionFlag.Preview | ActionFlag.Edit,
              dialog,
            })
          }}
        >
          <Trans>Preview</Trans>
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className='flex items-center'>
        <BackButton
          label={<Trans>Back</Trans>}
          bottomBorder
          onClick={() => {
            setDialogAction(undefined)
          }}
        />
        <div className='w-full border-b h-12 flex-c justify-end ltr:pr-4 rtl:pl-4 bdr text-m-sm text-primary-500'>
          <Trans>Editing</Trans> {dialogName}
        </div>
      </div>

      <Tabs activeKey={activeKey} defaultActiveKey={ComponentPanelTab.Data}>
        <Tabs.Nav
          onSelect={(eventKey) => {
            setActiveKey((eventKey as ComponentPanelTab) ?? ComponentPanelTab.Data)
          }}
        >
          <Tabs.NavItem eventKey={ComponentPanelTab.Data} className='mk-builder-component-nav-item'>
            <Trans>Data</Trans>
          </Tabs.NavItem>
          <Tabs.NavItem eventKey={ComponentPanelTab.Design} className='mk-builder-component-nav-item'>
            <Trans>Design</Trans>
          </Tabs.NavItem>
          <Tabs.NavItem eventKey={ComponentPanelTab.Action} className='mk-builder-component-nav-item'>
            <Trans>Action</Trans>
          </Tabs.NavItem>
          <Tabs.NavItem eventKey={ComponentPanelTab.Behavior} className='mk-builder-component-nav-item'>
            <Trans>Behavior</Trans>
          </Tabs.NavItem>
        </Tabs.Nav>

        <Tabs.Panel eventKey='data' mountOnEnter unmountOnExit noPadding>
          <SearchField search={searchData} setSearch={setSearchData} noBorder />
          {renderReviewButton()}
          <DialogSettingData dialog={dialog} search={searchData} />
        </Tabs.Panel>
        <Tabs.Panel eventKey='design' mountOnEnter unmountOnExit noPadding>
          <SearchField search={searchDesign} setSearch={setSearchDesign} noBorder />
          {renderReviewButton()}
          <DialogSettingDesign dialog={dialog} search={searchDesign} />
        </Tabs.Panel>
        <Tabs.Panel eventKey='action' mountOnEnter unmountOnExit noPadding>
          <SearchField search={searchAction} setSearch={setSearchAction} noBorder />
          {renderReviewButton()}
          <DialogSettingAction dialog={dialog} search={searchAction} />
        </Tabs.Panel>
        <Tabs.Panel eventKey='behavior' mountOnEnter unmountOnExit noPadding>
          <SearchField search={searchBehavior} setSearch={setSearchBehavior} noBorder />
          {renderReviewButton()}
          <DialogSettingBehavior dialog={dialog} search={searchBehavior} />
        </Tabs.Panel>
      </Tabs>
    </>
  )
}

export default DialogPanelEdit
